import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';
import { GrowthDistributionService, GrowthPublishJob } from './growth-distribution.service';
import {
  GROWTH_ARTICLE_PUBLISH_CHANNEL,
  buildStorefrontArticleUrl
} from './growth-article-publishing.util';
import {
  GrowthContentAsset,
  GrowthContentBrief,
  GrowthContentTemplatePack,
  GrowthMarketingService,
  GrowthPublishPlan
} from './growth-marketing.service';
import { isPublishPlanApproved, isPublishPlanTerminal, normalizePublishPlanStatus } from './growth-publish-plan.util';

export interface GrowthArticlePublishResult {
  content: GrowthContentAsset;
  plan: GrowthPublishPlan;
  jobs: GrowthPublishJob[];
  articleUrl: string;
}

@Injectable({ providedIn: 'root' })
export class GrowthArticlePublishingService {
  private readonly marketing = inject(GrowthMarketingService);
  private readonly distribution = inject(GrowthDistributionService);

  produceBrief(brief: GrowthContentBrief, templatePacks: GrowthContentTemplatePack[]): Observable<GrowthContentAsset> {
    const template = templatePacks.find(
      item => item.contentType === brief.contentType
        && item.ownerScope === brief.ownerScope
        && item.isActive
        && item.isCertified
    );

    if (!template) {
      return throwError(() => new Error('growth_article_publish_template_missing'));
    }

    return this.marketing.produceContentBrief(brief.id, {
      templatePackKey: template.key,
      idempotencyKey: `produce-${brief.id}-${Date.now()}`
    }).pipe(map(result => result.output));
  }

  getOrCreatePublishPlan(contentId: string, channelKeys: string | string[] = GROWTH_ARTICLE_PUBLISH_CHANNEL): Observable<GrowthPublishPlan> {
    const channels = Array.isArray(channelKeys) ? channelKeys : [channelKeys];
    const normalized = Array.from(
      new Set(
        [...channels, GROWTH_ARTICLE_PUBLISH_CHANNEL]
          .map(item => item.trim().toLowerCase())
          .filter(item => !!item)
      )
    );

    return this.marketing.getPublishPlan(contentId).pipe(
      catchError(() => of(null)),
      switchMap(plan => {
        if (plan && !isPublishPlanTerminal(plan.status)) {
          return of(plan);
        }

        return this.marketing.createPublishPlan(contentId, {
          consentScope: 'marketing',
          channelsJson: JSON.stringify(normalized)
        });
      })
    );
  }

  ensurePublishPlanApproved(plan: GrowthPublishPlan): Observable<GrowthPublishPlan> {
    if (isPublishPlanApproved(plan.status)) {
      return of(plan);
    }

    const status = normalizePublishPlanStatus(plan.status);
    if (status === 'Rejected') {
      return throwError(() => new Error('growth_publish_plan_rejected'));
    }

    return this.distribution.requestPublishPlanApproval(plan.id).pipe(
      switchMap(updated => {
        const resolved = updated as GrowthPublishPlan;
        if (isPublishPlanApproved(resolved.status)) {
          return of(resolved);
        }

        if (resolved.lastWorkflowDeferredCode && resolved.lastWorkflowDeferredCode.trim().length > 0) {
          return throwError(() => new Error(resolved.lastWorkflowDeferredCode!));
        }

        return throwError(() => new Error('growth_publish_plan_workflow_pending'));
      })
    );
  }

  publishToPublicStorefront(contentId: string, channelKey = GROWTH_ARTICLE_PUBLISH_CHANNEL): Observable<GrowthArticlePublishResult> {
    return this.marketing.ensureContentReadyForStorefrontApply(contentId).pipe(
      switchMap(content => this.getOrCreatePublishPlan(content.id, channelKey).pipe(
        switchMap(plan => this.ensurePublishPlanApproved(plan).pipe(
          switchMap(approvedPlan => {
            if (normalizePublishPlanStatus(approvedPlan.status) === 'Executed') {
              return of({
                content,
                plan: approvedPlan,
                jobs: [],
                articleUrl: buildStorefrontArticleUrl(content.slug ?? content.id.replace(/-/g, ''))
              });
            }

            return this.distribution.executePublishPlan(approvedPlan.id, {
              idempotencyKey: `article-publish-${content.id}-${Date.now()}`,
              correlationId: crypto.randomUUID()
            }).pipe(
              switchMap(jobs => this.marketing.getContent(content.id).pipe(
                map(updatedContent => ({
                  content: updatedContent,
                  plan: approvedPlan,
                  jobs,
                  articleUrl: buildStorefrontArticleUrl(updatedContent.slug ?? updatedContent.id.replace(/-/g, ''))
                }))
              ))
            );
          })
        ))
      ))
    );
  }
}
